import { AgentCreditCardMemberModel, PaymentOptionModel, PrebookHotelSummaryModel } from '@/__generated';
import { ModalUtils } from '@/contexts/ModalWrapper';
import { apiSdk } from '@/httpClient';
import { AppUrls } from '@/urls';
import React from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import history from '@/history';
import PageLoading from '@/components/App/PageLoading';
import BookingTimeout from '@/components/App/BookingTimeout';
import BookingHeader from '@/components/App/Header/BookingHeader';
import BookingStep from '@/components/App/BookingComponents/BookingStep';
import HotelSummary from '@/components/App/BookingComponents/HotelSummary';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ContactDetail from '@/components/App/BookingComponents/ContactDetail';
import ReadOnlyPassengerDetail from '@/components/App/BookingComponents/ReadOnlyPassengerDetail';
import { scrollToElementById } from '@/helpers/utils';
import LoadingModal from '@/components/App/Modals/LoadingModal';
import { handleApiUnKnowError, handleBookingNotFound, handleSubmitError } from '@/helpers/errorHandle';
import { motion } from 'framer-motion';
import PartialBookedConfirmModal from '@/components/App/BookingComponents/PartialBookedConfirmModal';
import bookingService from '@/services/bookingService';
import ReadOnlyGuestNumber from '@/components/App/BookingComponents/ReadOnlyGuestNumber';
import { useGetUseRole } from '@/hooks/useGetUserRole';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import ReadOnlyAgentBookingInfo from '@/components/App/BookingComponents/ReadOnlyAgentBookingInfo';

type Props = {};
const defaultPaymentOptions = ['Invoice', 'CreditCard_PayLater', 'CreditCard_PayNow'];

export default function PaymentPage({}: Props) {
  const { t } = useTranslation();
  const { sessionId } = useParams<{ sessionId: string }>();
  const [loading, setLoading] = React.useState<boolean>(true);
  const [paymentLoading, setPaymentLoading] = React.useState<boolean>(false);

  const [bookingSummary, setBookingSummary] = React.useState<PrebookHotelSummaryModel>();
  const [selectedPaymentMethod, setSelectedPaymentMethod] = React.useState<PaymentOptionModel>();
  const [selectedCard, setSelectedCard] = React.useState<AgentCreditCardMemberModel>();
  const [displayPartialBookedModal, setDisplayPartialBookedModal] = React.useState(false);
  const [bookingId, setBookingId] = React.useState<string>();
  const [creditCards, setCreditCards] = React.useState<AgentCreditCardMemberModel[]>([]);

  const history = useHistory();
  const { isAdmin, isBooking } = useGetUseRole();
  if (!isAdmin && !isBooking) {
    history.push(AppUrls.Page401);
    return null;
  }

  React.useEffect(() => {
    if (sessionId) {
      getHotelBookingSummary(sessionId);
      getCreditCards();
    } else {
      history.push(AppUrls.HomePage);
    }
  }, [sessionId]);

  React.useEffect(() => {
    if (bookingSummary?.paymentOptions?.length) {
      for (var i = 0; i < defaultPaymentOptions.length; i++) {
        let _payOption = bookingSummary?.paymentOptions.find(x => x.name === defaultPaymentOptions[i]);
        if (_payOption) {
          setSelectedPaymentMethod(_payOption);
          return;
        }
      }
    }
  }, [bookingSummary]);
  const getCreditCards = () => {
    apiSdk.BookingService.getAgentCreditCardMembers()
      .then(res => {
        setCreditCards(res);
      })
      .catch(handleSubmitError);
  };
  React.useEffect(() => {
    const onWindowActive = () => {
      if (!document.hidden) {
        getCreditCards();
      }
    };
    document.addEventListener('visibilitychange', onWindowActive);
    return () => {
      document.removeEventListener('visibilitychange', onWindowActive);
    };
  }, []);
  const getHotelBookingSummary = (sId: string) => {
    setLoading(true);
    apiSdk.BookingService.getPrebookHotelSummary({ sessionId: sId })
      .then(res => {
        if (!res.sessionExpired && res.selectedHotel && (res.ttl || 0) > 0) {
          setBookingSummary(res);
        } else {
          handleBookingNotFound();
        }
      })
      .catch(() => {
        handleBookingNotFound();
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const onTimeout = () => {
    handleBookingNotFound();
  };

  const payNow = () => {
    if (!selectedPaymentMethod) {
      ModalUtils.error({
        title: 'Please select payment method!',
        onClose: () => scrollToElementById(null, 'booking-payment-method'),
      });
      return;
    }
    if (selectedPaymentMethod.creditCardRequired) {
      if (!selectedCard) {
        ModalUtils.error({
          title: 'Please select your credit card!',
          onClose: () => scrollToElementById(null, 'booking-select-credit-card'),
        });
        return;
      }
    }
    setPaymentLoading(true);
    apiSdk.BookingService.completeBooking({
      body: {
        creditCardMemberId: selectedCard?.memberId,
        paymentOptionName: selectedPaymentMethod.name || '',
        sessionId: sessionId,
      },
    })
      .then(res => {
        const handlePartialBooking = () => {
          setDisplayPartialBookedModal(true);
          setBookingId(res.bookingId);
        };
        bookingService.handleCompleteBooking(res, handlePartialBooking);
      })
      .catch(handleApiUnKnowError)
      .finally(() => {
        setPaymentLoading(false);
      });
  };

  if (loading) {
    return <PageLoading />;
  }

  if (!bookingSummary) {
    return null;
  }
  let { contactDetail, leadPassenger, paymentOptions } = bookingSummary;

  return (
    <div className="mb-50">
      <Helmet>
        <title>{`Payment Details - ${bookingSummary.sessionId}`}</title>
      </Helmet>
      <BookingHeader isSticky />
      <div className="header-margin" />
      <BookingTimeout ttl={bookingSummary.ttl || 0} onTimeout={onTimeout} />
      {displayPartialBookedModal && bookingId ? (
        <PartialBookedConfirmModal defaultConfirm={true} bookingId={bookingId} onClose={() => setDisplayPartialBookedModal(false)} />
      ) : null}

      {paymentLoading ? <LoadingModal title={t('LOADING_MODAL_MESSAGE')} subTitle={t('DO_NOT_CLOSE_BROWSER')} /> : null}
      <div className="container mt-20">
        <BookingStep activeStep={2} />
        <div className="row x-gap-50 mt-20 y-gap-20">
          <div className="col-xl-7 col-lg-8 mt-30 col-md-12 col-xs-12 sm:order-2 md:order-2">
            <div className="row x-gap-10 y-gap-20 mb-20" id="booking-payment-method">
              <div className="col-12 d-flex align-items-center mb-10">
                <div className="fw-bold text-18 text-blue-1  rounded-2 pr-10 text-uppercase">Payment method</div>
                <div style={{ flex: 1, border: '1px dashed #cccccc' }} />
              </div>
              {paymentOptions?.map(paymentOption => {
                const isActive = paymentOption?.name === selectedPaymentMethod?.name;
                const activeClass = isActive ? 'bg-green-2 text-white border-light' : 'bg-white border-dark-4 text-dark-4';
                return (
                  <div className={`col-md-4 col-xs-12 `} key={paymentOption.name}>
                    <button
                      onClick={() => setSelectedPaymentMethod(paymentOption)}
                      className={`transition-cubic button rounded px-20 py-20 d-flex items-center h-100 ${activeClass} w-100`}
                    >
                      <span className="text-18 ">{paymentOption.displayName}</span>
                    </button>
                  </div>
                );
              })}
            </div>
            {selectedPaymentMethod?.creditCardRequired ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{
                  duration: 1,
                  delay: 0.1,
                  ease: [0, 0.71, 0.2, 1.01],
                }}
                className="row x-gap-10 y-gap-20 mb-20"
                id="booking-select-credit-card"
              >
                <div className="col-12 d-flex align-items-center mb-10">
                  <div className="fw-bold text-18 text-blue-1  rounded-2 pr-10 text-uppercase">Your preferred credit card</div>
                  <div style={{ flex: 1, border: '1px dashed #cccccc' }} />
                </div>
                {creditCards?.length ? (
                  creditCards?.map(card => {
                    const isActive = selectedCard?.memberId === card?.memberId;
                    const activeClass = isActive ? 'bg-green-2 text-white border-light' : ' border-dark-4';
                    return (
                      <div className="col-md-3 col-xs-12" key={card.id}>
                        <div
                          onClick={() => {
                            setSelectedCard(card);
                          }}
                          className={`transition-cubic text-center tran rounded-2 w-100 cursor-pointer text-14 ${activeClass}`}
                        >
                          <div className="fw-500 text-14 mt-10">{card.cardNo}</div>
                          <div className="text-10 mb-10">{card.memberId}</div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="px-20 py-50 text-center">
                    <div className="fw-500">You don`t have any credit cards.</div>
                    <div className="text-12 ">
                      Please create new credit card in your
                      <a target="_blank" href={AppUrls.AgentProfile} className="text-blue-1 ml-5">
                        agent profile page
                      </a>
                    </div>
                  </div>
                )}
              </motion.div>
            ) : null}

            <ContactDetail contactDetail={contactDetail} />
            <ReadOnlyPassengerDetail passenger={leadPassenger} />
            <ReadOnlyGuestNumber bookingSummary={bookingSummary} />
            <ReadOnlyAgentBookingInfo bookingSummary={bookingSummary} />
            <div className="mt-30 mt-30 sm:d-none md:d-none">
              <Link to={AppUrls.__createBookingPassengerUrl(sessionId)} className="text-red-1 fw-500 text-14 underline d-flex items-center">
                <FontAwesomeIcon icon={faChevronLeft} className="mr-5" />
                Back to passenger detail
              </Link>
            </div>
          </div>
          <div className="col-xl-5 col-lg-4 col-md-12 col-xs-12 order-3 sm:order-1 md:order-1">
            <HotelSummary bookingSummary={bookingSummary} onPayment={payNow} />
          </div>
        </div>
      </div>
    </div>
  );
}
